import { useReducer, useEffect } from 'react';
import type { QueryOptions } from 'Utils/graphql-client/graphqlClient';
import graphqlClient from 'Utils/graphql-client/graphqlClient';
import { useAppContext } from 'Context/ReactAppContext';
type QueryType = {
  query: string;
  cache?: boolean;
  map?: (e: any) => any;
  catch?: (e: any) => any;
  queryOptions: QueryOptions;
  ignore?: boolean;
};

const noop = (e) => e;

function reducer(state, action) {
  switch (action.type) {
    case 'loading':
      return { ...state, isLoading: true };

    case 'data':
      return { ...state, data: action.data, isLoading: false };

    case 'error':
      return { ...state, error: action.error, isLoading: false };

    default:
      throw new Error(`Unknown action type! [${action.type}]`);
  }
}

export function useGQL<T>(...queries: Array<QueryType>): {
  isLoading: boolean;
  data: T | null | undefined;
  error: any;
} {
  const initialState = {
    data: null,
    error: null,
    isLoading: true,
  };
  const [state, dispatch] = useReducer(reducer, initialState);
  const appContext = useAppContext();
  const filteredQueries = queries.filter((query) => !query.ignore);
  useEffect(
    () => {
      dispatch({
        type: 'loading',
      });
      const promises = filteredQueries.map(
        ({ query, cache = false, map = noop, queryOptions, catch: catchCallback }) => {
          const promise = cache
            ? graphqlClient.runQuery(query, appContext, queryOptions)
            : graphqlClient.runNoCacheQuery(query, appContext, queryOptions);

          if (catchCallback) {
            return promise.then(map).catch(catchCallback);
          }

          return promise.then(map);
        },
      );
      Promise.all(promises)
        .then((datas) => {
          if (filteredQueries.length > 0) {
            const data =
              filteredQueries.length === 1
                ? datas[0]
                : datas.reduce((acc, item) => ({ ...acc, ...item }), {});
            dispatch({
              type: 'data',
              data,
            });
          } else {
            dispatch({
              type: 'data',
              data: undefined,
            });
          }
        })
        .catch((error) => {
          dispatch({
            type: 'error',
            error,
          });
        });
    },
    queries.map((q) => q.query + (q.ignore ? 'T' : 'F')),
  );
  // useEffect run at each render on the react lifecycle. Here the useEffect should run only if the query OR the ignore param change!
  return state;
}
